import React, { useState } from "react";
import {
  Box,
  Card,
  IconButton,
  InputBase,
  useTheme,
  Chip,
  Divider,
  Typography,
  Paper,
} from "@mui/material";
import { Search, CameraAlt, Refresh } from "@mui/icons-material";
import Scanner from "components/Scanner";
import TrackingDetail from "components/TrackingDetail";
import TrackingSummaryItem from "components/TrackingSummaryItem";
import { getScanText } from "utils";
import { BG_TINT } from "components/bento/bentoTokens";

const MAX_TRACKING = 10;

const parseInput = (input) =>
  input
    .split(/[,\n;]+/)
    .map((s) => getScanText(s.trim()))
    .filter(Boolean)
    .slice(0, MAX_TRACKING);

const ClientTracking = () => {
  const theme = useTheme();
  const secondary = theme.palette.secondary.main;

  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [trackingIds, setTrackingIds] = useState([]);

  const handleSearch = (rawValue = inputValue) => {
    const ids = parseInput(rawValue);
    setTrackingIds(ids);
    if (ids.length === 1) setInputValue(ids[0]);
  };

  const parsedCount = parseInput(inputValue).length;
  const isSingle = trackingIds.length === 1;
  const isMultiple = trackingIds.length > 1;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box mb={3}>
        <Typography variant="h4" fontWeight="600" color="secondary" sx={{ mb: 1 }}>
          Tracking
        </Typography>
        <Typography variant="body1" color="text.secondary">
          ໃສ່ໝາຍເລກ Tracking number ເພື່ອກວດສອບສະຖານະພັດສະດຸ (ໃສ່ຫຼາຍເລກໂດຍຄັ່ນດ້ວຍ , ຫຼື Enter)
        </Typography>
      </Box>

      {/* Search bar */}
      <Card
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1.25,
          mb: 3,
          maxWidth: 560,
          borderRadius: 3,
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          border: `1px solid ${theme.palette.divider}`,
          transition: "border-color 0.2s, box-shadow 0.2s",
          "&:focus-within": {
            borderColor: secondary,
            boxShadow: `0 0 0 3px ${secondary}18`,
          },
        }}
      >
        <Search sx={{ color: theme.palette.text.disabled, flexShrink: 0, alignSelf: "flex-start", mt: "8px" }} />
        <InputBase
          id="trackingInput"
          placeholder="Tracking number (comma or Enter to separate multiple)"
          fullWidth
          multiline
          maxRows={6}
          autoFocus
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            if (!e.target.value) setTrackingIds([]);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSearch(e.target.value);
            }
          }}
          sx={{ fontSize: "0.95rem" }}
        />
        {parsedCount > 1 && (
          <Chip
            label={parsedCount}
            size="small"
            color="secondary"
            sx={{ height: 22, minWidth: 28, fontSize: "0.75rem", fontWeight: 700 }}
          />
        )}
        <IconButton onClick={() => setOpen(true)} size="small" sx={{ color: "text.secondary" }}>
          <CameraAlt fontSize="small" />
        </IconButton>
        <IconButton
          onClick={() => handleSearch()}
          sx={{
            bgcolor: secondary,
            color: "white",
            borderRadius: 1.5,
            px: 1.5,
            py: 0.5,
            flexShrink: 0,
            "&:hover": { bgcolor: theme.palette.secondary.dark },
          }}
        >
          <Search fontSize="small" />
        </IconButton>
      </Card>

      {/* Results */}
      {trackingIds.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            maxWidth: 650,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            px={2}
            pt={1.5}
            pb={0.75}
          >
            <Typography variant="caption" color="text.secondary">
              {isMultiple
                ? `${trackingIds.length} tracking numbers`
                : <>Tracking: <strong>{trackingIds[0]}</strong></>}
            </Typography>
            <IconButton size="small" onClick={() => handleSearch(inputValue)} sx={{ color: "text.secondary" }}>
              <Refresh fontSize="small" />
            </IconButton>
          </Box>
          <Divider />

          {isSingle && (
            <Box
              sx={{
                "& > div > .MuiPaper-root": {
                  maxWidth: "100%",
                  borderRadius: 0,
                  boxShadow: "none",
                  backgroundColor: BG_TINT,
                  mt: 0,
                },
              }}
            >
              <TrackingDetail trackingNumber={trackingIds[0]} masked={true} />
            </Box>
          )}

          {isMultiple && (
            <Box>
              {trackingIds.map((id, i) => (
                <React.Fragment key={id}>
                  <TrackingSummaryItem trackingId={id} masked={true} />
                  {i < trackingIds.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </Box>
          )}
        </Paper>
      )}

      <Scanner
        open={open}
        setOpen={setOpen}
        closeAfterResult={true}
        onDecode={(result) => {
          if (result) {
            const clean = getScanText(result);
            const current = parseInput(inputValue);
            const merged = [...new Set([...current, clean])].slice(0, MAX_TRACKING);
            const newInput = merged.join(", ");
            setInputValue(newInput);
            setOpen(false);
            handleSearch(newInput);
          }
        }}
      />
    </Box>
  );
};

export default ClientTracking;
